// src/components/PolicyManagementNew.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, doc, deleteDoc, query, where } from 'firebase/firestore';
import { Loader2, AlertCircle, Plus } from 'lucide-react';
import Card from './Card';  // <-- ADD THIS LINE
import PolicyFormNew from './PolicyFormNew';
import PolicyCardNew from './PolicyCardNew';

const PolicyManagementNew = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [showNewPolicyForm, setShowNewPolicyForm] = useState(false);

  // Fetch wrapper policies on mount
  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      // Query for wrapper policies only
      const q = query(
        collection(db, 'policies'),
        where('accessMethod', '==', 'wrapper')
      );
      const querySnapshot = await getDocs(q);
      const policiesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPolicies(policiesData);
    } catch (err) {
      console.error('Error fetching wrapper policies:', err);
      setError('Failed to fetch policies');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePolicy = async (policyData, policyId = null) => {
    try {
      // Ensure the policy has the wrapper access method
      const dataToSave = {
        ...policyData,
        accessMethod: 'wrapper'
      };

      if (policyId) {
        // Update existing policy
        await updateDoc(doc(db, 'policies', policyId), dataToSave);
        setPolicies(prev => prev.map(p => 
          p.id === policyId ? { ...p, ...dataToSave } : p
        ));
      } else {
        // Create new policy
        const docRef = await addDoc(collection(db, 'policies'), dataToSave);
        setPolicies(prev => [...prev, { id: docRef.id, ...dataToSave }]);
      }

      setEditingPolicy(null);
      setShowNewPolicyForm(false);
      return true;
    } catch (error) {
      console.error('Error saving policy:', error);
      throw error;
    }
  };

  const handleDeletePolicy = async (policyId) => {
    try {
      if (!window.confirm('Are you sure you want to delete this policy?')) {
        return;
      }

      await deleteDoc(doc(db, 'policies', policyId));
      setPolicies(prev => prev.filter(p => p.id !== policyId));
      return true;
    } catch (error) {
      console.error('Error deleting policy:', error);
      setError('Failed to delete policy');
      return false;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!showNewPolicyForm && !editingPolicy && (
        <button
          onClick={() => setShowNewPolicyForm(true)}
          className="inline-flex items-center px-4 py-2 text-sm text-white bg-primary rounded-md hover:bg-primary/90"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Wrapper Policy
        </button>
      )}

      {(showNewPolicyForm || editingPolicy) && (
        <PolicyFormNew
          policy={editingPolicy}
          onSave={handleSavePolicy}
          onCancel={() => {
            setEditingPolicy(null);
            setShowNewPolicyForm(false);
          }}
        />
      )}

      {error && (
        <div className="flex items-center space-x-2 text-red-500 p-4">
          <AlertCircle className="h-5 w-5" />
          <p>{error}</p>
        </div>
      )}

      <div className="space-y-4">
        {policies.length === 0 ? (
          <Card>
            <div className="p-6 text-center text-gray-500">
              No wrapper policies created yet. Click "New Wrapper Policy" to get started.
            </div>
          </Card>
        ) : (
          policies.map(policy => (
            <PolicyCardNew
              key={policy.id}
              policy={policy}
              onEdit={() => {
                setShowNewPolicyForm(false);
                setEditingPolicy(policy);
              }}
              onDelete={() => handleDeletePolicy(policy.id)}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default PolicyManagementNew;